import { createThunklessAction } from 'thunkless';
import { avo } from '@/api/app/avo-client';
import { TcrTypes, TcrBrand, TcrCampaign } from '../../../types/tcr';

interface ArchiveTcrCampaignProps {
  id: string;
  tcr_campaign_hash_id: string;
}

export interface FetchTcrPropsProps { subdomain: string }

export const fetchTcrProps = ({ subdomain }: FetchTcrPropsProps) => createThunklessAction({
  type: [
    TcrTypes.FETCH_TCR_PROPS_REQUEST,
    TcrTypes.FETCH_TCR_PROPS_SUCCESS,
    TcrTypes.FETCH_TCR_PROPS_FAILURE,
  ] as const,
  promise: () => avo.ajax({
    method: 'GET',
    route: `/accounts/${subdomain}/tcr_props`,
  }),
});

export const liveUpdateTcrBrand = (events: any[]) => ({
  type: TcrTypes.LIVE_UPDATE_TRC_BRAND_REQUEST,
  meta: { events },
});

type UpdateTcrBrandKey = 'first_name' | 'last_name' | 'display_name' | 'phone' | 'street' | 'city' | 'state' | 'postal_code' | 'country' | 'email' | 'stock_symbol' | 'stock_exchange' | 'website' | 'alt_business_id' | 'alt_business_id_type' | 'vertical' | 'privacy_policy';

export const updateTcrBrandKeys: UpdateTcrBrandKey[] = [
  'first_name',
  'last_name',
  'display_name',
  'phone',
  'street',
  'city',
  'state',
  'postal_code',
  'email',
  'stock_symbol',
  'stock_exchange',
  'website',
  'privacy_policy',
  'alt_business_id',
  'alt_business_id_type',
  'vertical',
];

type UpdateFilteredTcrBrand = Pick<TcrBrand, UpdateTcrBrandKey>;

type CreateTcrBrandKey = UpdateTcrBrandKey | 'entity_type' | 'reference_id' | 'ein' | 'ein_issuing_country' | 'company_name';

export const createTcrBrandKeys: CreateTcrBrandKey[] = [
  ...updateTcrBrandKeys,
  'country',
  'entity_type',
  'reference_id', // sole-prop ein/ssn
  'ein',
  'ein_issuing_country',
  'company_name',
  'privacy_policy',
];

type CreateFilteredTcrBrand = Pick<TcrBrand, CreateTcrBrandKey>;

export interface CreateTcrBrandProps {
  orgId: string;
  tcrBrandId: TcrBrand['id'];
  tcrBrand: CreateFilteredTcrBrand;
}

export interface UpdateTcrBrandProps {
  orgId: string;
  tcrBrandId: TcrBrand['id'];
  tcrBrand: UpdateFilteredTcrBrand;
}

export const registerTcrBrand = ({
  orgId, tcrBrand, tcrBrandId,
}: CreateTcrBrandProps) => (
  createThunklessAction({
    type: [
      TcrTypes.REGISTER_TCR_BRAND_REQUEST,
      TcrTypes.REGISTER_TCR_BRAND_SUCCESS,
      TcrTypes.REGISTER_TCR_BRAND_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${orgId}/tcr_brand/register`,
      payload: { id: tcrBrandId, tcr_brand: tcrBrand },
    }),
  })
);

export interface CreateFirstTcrBrandProps {
  orgId?: string;
  tcrBrandId?: TcrBrand['id'];
  tcrBrand?: CreateFilteredTcrBrand;
  campaignInfo?: Partial<TcrCampaign>;
}
export const registerFirstSubmission = ({
  orgId, tcrBrand, tcrBrandId, campaignInfo,
}: CreateFirstTcrBrandProps) => (
  createThunklessAction({
    type: [
      TcrTypes.FIRST_SUBMISSION_TCR_BRAND_REQUEST,
      TcrTypes.FIRST_SUBMISSION_TCR_BRAND_SUCCESS,
      TcrTypes.FIRST_SUBMISSION_TCR_BRAND_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${orgId}/tcr_brand/first_submission`,
      payload: { id: tcrBrandId, tcr_brand: tcrBrand, first_submission_campaign: campaignInfo },
    }),
    meta: { campaignInfo },
  })
);


export const updateTcrBrand = ({
  orgId, tcrBrand, tcrBrandId,
}: UpdateTcrBrandProps) => (
  createThunklessAction({
    type: [
      TcrTypes.TCR_BRAND_UPDATE_REQUEST,
      TcrTypes.TCR_BRAND_UPDATE_SUCCESS,
      TcrTypes.TCR_BRAND_UPDATE_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'PUT',
      route: `/organizations/${orgId}/tcr_brand/${tcrBrandId}`,
      payload: { tcr_brand: tcrBrand },
    }),
  })
);

interface PostTcrBrandProps {
  id: string;
  tcrBrandId: string;
}

export const vetTcrBrand = ({ id, tcrBrandId }: PostTcrBrandProps) => (
  createThunklessAction({
    type: [
      TcrTypes.VET_TCR_BRAND_REQUEST,
      TcrTypes.VET_TCR_BRAND_SUCCESS,
      TcrTypes.VET_TCR_BRAND_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${id}/tcr_brand/${tcrBrandId}/vet`,
    }),
  })
);

export const resubmitTcrBrand = ({ id, tcrBrandId }: PostTcrBrandProps) => (
  createThunklessAction({
    type: [
      TcrTypes.RESUBMIT_TCR_BRAND_REQUEST,
      TcrTypes.RESUBMIT_TCR_BRAND_SUCCESS,
      TcrTypes.RESUBMIT_TCR_BRAND_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${id}/tcr_brand/${tcrBrandId}/revet`,
    }),
  })
);

type RegisterTcrCampaignAttrs = Pick<TcrCampaign, 'usecase' | 'sample_1' | 'sample_2' | 'description' | 'direct_lending' | 'embedded_link' | 'embedded_phone' | 'age_gated' | 'website' | 'message_flow_opt_in_methods'>;

interface RegisterTcrCampaignProps extends RegisterTcrCampaignAttrs {
  org_id: string;
  tcr_campaign_hash_id?: string;
}

export const registerTcrCampaign = ({
  org_id,
  tcr_campaign_hash_id,
  message_flow_opt_in_methods,
  ...rest
}: RegisterTcrCampaignProps) => (
  createThunklessAction({
    type: [
      TcrTypes.CREATE_TCR_CAMPAIGN_REQUEST,
      TcrTypes.CREATE_TCR_CAMPAIGN_SUCCESS,
      TcrTypes.CREATE_TCR_CAMPAIGN_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${org_id}/tcr_campaigns/register`,
      payload: {
        tcr_campaign_hash_id,
        message_flow_opt_in_methods: JSON.stringify(message_flow_opt_in_methods),
        ...rest,
      },
    }),
  })
);

type UpdateTcrCampaignAttrs = Pick<TcrCampaign, 'sample_1' | 'sample_2' | 'description' | 'message_flow_opt_in_methods' | 'website' | 'terms_and_conditions'>;

interface UpdateTcrCampaignProps extends UpdateTcrCampaignAttrs {
  org_id: string;
  tcr_campaign_hash_id: string;
}

export const updateTcrCampaign = ({
  org_id,
  tcr_campaign_hash_id,
  message_flow_opt_in_methods,
  ...rest
}: UpdateTcrCampaignProps) => (
  createThunklessAction({
    type: [
      TcrTypes.UPDATE_TCR_CAMPAIGN_REQUEST,
      TcrTypes.UPDATE_TCR_CAMPAIGN_SUCCESS,
      TcrTypes.UPDATE_TCR_CAMPAIGN_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'PATCH',
      route: `/organizations/${org_id}/tcr_campaigns/${tcr_campaign_hash_id}`,
      payload: {
        message_flow_opt_in_methods: JSON.stringify(message_flow_opt_in_methods),
        ...rest,
      },
    }),
  })
);

export const archiveTcrCampaign = ({
  id, tcr_campaign_hash_id,
}: ArchiveTcrCampaignProps) => (
  createThunklessAction({
    type: [
      TcrTypes.ARCHIVE_TCR_CAMPAIGN_REQUEST,
      TcrTypes.ARCHIVE_TCR_CAMPAIGN_SUCCESS,
      TcrTypes.ARCHIVE_TCR_CAMPAIGN_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'PATCH',
      route: `/organizations/${id}/tcr_campaigns/${tcr_campaign_hash_id}/archive`,
    }),
  })
);

export const clearArchiveTcrCampaignStatus = () => ({
  type: TcrTypes.CLEAR_ARCHIVE_TCR_CAMPAIGN_STATUS,
});

export const clearUpdateTcrCampaignStatus = () => ({
  type: TcrTypes.CLEAR_UPDATE_TCR_CAMPAIGN_STATUS,
});

export const clearTcrCampaignStatus = () => ({
  type: TcrTypes.CLEAR_TCR_CAMPAIGN_STATUS,
});

export const clearTcrBrandStatus = () => ({
  type: TcrTypes.CLEAR_TCR_BRAND_STATUS,
});

export const clearTcrBrandUpdateStatus = () => ({
  type: TcrTypes.CLEAR_TCR_BRAND_UPDATE_STATUS,
});

export const clearVetTcrBrandStatus = () => ({
  type: TcrTypes.CLEAR_VET_TCR_BRAND_STATUS,
});

export const clearCreateExternalCampaignStatus = () => ({
  type: TcrTypes.CLEAR_CREATE_EXTERNAL_CAMPAIGN_STATUS,
});

export const clearTcrNotification = () => ({
  type: TcrTypes.CLEAR_TCR_NOTIFICATION,
});

export const clearInboxSyncStatus = (tcrCampaignId: TcrCampaign['id']) => ({
  type: TcrTypes.CLEAR_CAMPAIGN_INBOX_SYNC_STATUS,
  meta: { tcrCampaignId },
});

interface SyncInboxesProps {
  id: string;
  accountHashIds: string[];
  accountSubdomains: string[];
  tcrCampaignId: string;
}

export const syncInboxes = ({
  id, tcrCampaignId, accountHashIds, accountSubdomains,
}: SyncInboxesProps) => (
  createThunklessAction({
    type: [
      TcrTypes.SYNC_INBOXES_REQUEST,
      TcrTypes.SYNC_INBOXES_SUCCESS,
      TcrTypes.SYNC_INBOXES_FAILURE,
    ] as const,
    promise: () => avo.ajax<void>({
      method: 'POST',
      route: `/organizations/${id}/tcr_campaigns/${tcrCampaignId}/sync_inboxes`,
      payload: { account_subdomains: accountSubdomains },
    }),
    meta: { tcrCampaignId, accountHashIds },
  })
);

interface UnsyncInboxesProps {
  subdomain: string;
  tcrCampaignId: TcrCampaign['campaign_id'];
}

interface NudgeTcrCampaignProps {
  orgId: string;
  tcrCampaignId: string;
  description: string;
  nudgeIntent: string;
}

export const nudgeTcrCampaign = ({
  orgId,
  tcrCampaignId,
  description,
  nudgeIntent,
}: NudgeTcrCampaignProps) => (
  createThunklessAction({
    type: [
      TcrTypes.NUDGE_TCR_CAMPAIGN_REQUEST,
      TcrTypes.NUDGE_TCR_CAMPAIGN_SUCCESS,
      TcrTypes.NUDGE_TCR_CAMPAIGN_FAILURE,
    ] as const,
    promise: () => avo.ajax({
      method: 'POST',
      route: `/organizations/${orgId}/tcr_campaigns/${tcrCampaignId}/nudge`,
      payload: { description, nudge_intent: nudgeIntent },
    }),
  })
);

export const unsyncInbox = ({
  subdomain, tcrCampaignId,
}: UnsyncInboxesProps) => (
  createThunklessAction({
    type: [
      TcrTypes.UNSYNC_INBOX_REQUEST,
      TcrTypes.UNSYNC_INBOX_SUCCESS,
      TcrTypes.UNSYNC_INBOX_FAILURE,
    ] as const,
    promise: () => avo.ajax<void>({
      method: 'POST',
      route: `/accounts/${subdomain}/unsync`,
    }),
    meta: { subdomain, tcrCampaignId },
  })
);

export const clearUnsyncInboxStatus = () => ({
  type: TcrTypes.CLEAR_UNSYNC_INBOX_STATUS,
});

export const clearResubmitBrandStatus = () => ({
  type: TcrTypes.CLEAR_RESUBMIT_TCR_BRAND_STATUS,
});

export const clearNudgeTcrCampaignStatus = () => ({
  type: TcrTypes.CLEAR_NUDGE_TCR_CAMPAIGN_STATUS,
});
